import React from "react"
import {
  Drawer,
  Box,
  Typography,
  Stack,
  Divider,
  useTheme,
  IconButton,
  CloseIcon,
  Button,
  UnorderedList,
  UnorderedListItem,
  ListItemText,
  Link
} from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl, FormattedDate } from "react-intl"
import moment from "moment/moment"
import { useFlags } from "launchdarkly-react-client-sdk"
import { ExpenseState } from "#/utils/ExpenseState"
import { ExpenseType } from "#/utils/ExpenseType"
import { Reimbursement } from "#/types/reimbursement"
import { getFileOnClick } from "#/components/views/reimbursements/upload/Attachments"

interface TransactionDetailsDrawerProps {
  open: boolean
  onClose: () => void
  transaction: Reimbursement
}

export const TransactionDetailsDrawer: React.FC<TransactionDetailsDrawerProps> = ({ open, onClose, transaction }) => {
  const intl = useIntl()
  const theme = useTheme()
  const { transactionDetailsDrawerForOopAndCardEnabled } = useFlags()

  if (!transaction) {
    return null
  }

  // Check expense type
  const isDirectPayExpense = transaction.expenseType === ExpenseType.DIRECT_PAY
  const isOopExpense = transaction.expenseType === ExpenseType.OOP
  const isCardExpense = transaction.expenseType === ExpenseType.CARD

  // Determine if drawer should be shown
  // DIRECTPAY always shows, OOP/CARD show when feature flag is enabled
  const shouldShowDrawer =
    isDirectPayExpense || (transactionDetailsDrawerForOopAndCardEnabled && (isOopExpense || isCardExpense))

  if (!shouldShowDrawer) {
    return null
  }

  // For OOP and CARD expenses, show empty drawer shell (content will be added in subsequent tickets)
  const shouldShowFullContent = isDirectPayExpense

  // Only calculate these for DIRECTPAY expenses to avoid accessing properties that may not exist for OOP/CARD
  const createdDateStringToDate = isDirectPayExpense ? moment(transaction.submittedDatetime).toDate() : null
  const serviceDateStringToDate = isDirectPayExpense ? moment(transaction.serviceDate).toDate() : null

  const getStatusText = () => {
    switch (transaction.expenseState) {
      case ExpenseState.SCHEDULED:
        return intl.formatMessage({ defaultMessage: "Scheduled" })
      case ExpenseState.CANCELLED:
        return (
          intl.formatMessage({ defaultMessage: "Canceled on" }) +
          " " +
          intl.formatDate(moment(transaction.deniedDatetime).toDate(), {
            year: "numeric",
            month: "short",
            day: "2-digit"
          })
        )
      case ExpenseState.PAID:
        return (
          intl.formatMessage({ defaultMessage: "Paid on" }) +
          " " +
          intl.formatDate(moment(transaction.paidDatetime).toDate(), {
            year: "numeric",
            month: "short",
            day: "2-digit"
          })
        )
      case ExpenseState.DENIED:
        return (
          intl.formatMessage({ defaultMessage: "Denied on" }) +
          " " +
          intl.formatDate(moment(transaction.deniedDatetime).toDate(), {
            year: "numeric",
            month: "short",
            day: "2-digit"
          })
        )
      case ExpenseState.ON_HOLD:
        return intl.formatMessage({ defaultMessage: "On hold" })
      case ExpenseState.RECEIVED:
        return intl.formatMessage({ defaultMessage: "Received" })
      case ExpenseState.NEEDS_PROVIDER_INFO:
        return intl.formatMessage({ defaultMessage: "Needs provider info" })
      case ExpenseState.NEEDS_MEMBER_UPLOADS:
        return intl.formatMessage({ defaultMessage: "Needs member uploads" })
      case ExpenseState.BILL_SUBMITTED:
      case ExpenseState.APPROVED:
      case ExpenseState.INVOICING:
      case ExpenseState.INVOICED:
        return intl.formatMessage(
          { defaultMessage: "Created on {createdDate}" },
          {
            createdDate: intl.formatDate(createdDateStringToDate, {
              year: "numeric",
              month: "short",
              day: "2-digit"
            })
          }
        )
      case ExpenseState.PENDING_NEXT_PLAN_YEAR:
        return intl.formatMessage({ defaultMessage: "Pending next plan year" })
      default:
        return transaction.expenseState
    }
  }

  const getWhoReceivedCare = () => {
    switch (transaction.whoReceivedCare) {
      case "MEMBER":
        return intl.formatMessage({ defaultMessage: "Member" })
      case "PARTNER":
        return intl.formatMessage({ defaultMessage: "Partner" })
      default:
        return intl.formatMessage({ defaultMessage: "Member" })
    }
  }

  const amountAppliedToDeductibleString = transaction.amountAppliedToDeductible
    ? intl.formatNumber(transaction.amountAppliedToDeductible / 100, {
        style: "currency",
        currency: transaction.currencyCode,
        currencyDisplay: "narrowSymbol",
        maximumFractionDigits: 0
      })
    : null

  const amountAppliedToCostShareString = transaction.amountAppliedToCostShare
    ? intl.formatNumber(transaction.amountAppliedToCostShare / 100, {
        style: "currency",
        currency: transaction.currencyCode,
        currencyDisplay: "narrowSymbol",
        maximumFractionDigits: 0
      })
    : null

  const amountOverAvailableFunds =
    transaction.eligibleAmount -
    transaction.amount -
    transaction.amountAppliedToDeductible -
    transaction.amountAppliedToCostShare

  const amountOverAvailableFundsString = amountOverAvailableFunds
    ? intl.formatNumber(amountOverAvailableFunds / 100, {
        style: "currency",
        currency: transaction.currencyCode,
        currencyDisplay: "narrowSymbol",
        maximumFractionDigits: 0
      })
    : null

  const FileLinks = () => {
    return (
      <>
        {transaction.fileGuids.map((guid, i) => (
          <Box paddingBottom={theme.tokens.spacing.md} key={i}>
            <Link component="button" onClick={getFileOnClick(guid)}>
              {transaction.fileNames[i]}
            </Link>
          </Box>
        ))}
      </>
    )
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={(theme) => ({
        "& .MuiDrawer-paper": {
          inlineSize: { xs: "100%", sm: "400px" },
          padding: theme.tokens.spacing.lg,
          display: "flex",
          flexDirection: "column",
          blockSize: "100%"
        }
      })}
    >
      {/* Header - Fixed at top */}
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={theme.tokens.spacing.lg}>
        <Typography variant="h4" component="h2" color="secondary">
          <FormattedMessage defaultMessage="Expense details" />
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label={intl.formatMessage({ defaultMessage: "Close expense details" })}
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            "&:hover": {
              color: theme.palette.text.primary
            }
          })}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {shouldShowFullContent ? (
          <Stack spacing={theme.tokens.spacing.lg}>
            {/* Status */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Status" />
              </Typography>
              <Typography variant="body1">{getStatusText()}</Typography>
            </Box>

            {/* Created Date */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Created date" />
              </Typography>
              <Typography variant="body1">
                <FormattedDate value={createdDateStringToDate} year="numeric" month="long" day="2-digit" />
              </Typography>
            </Box>

            {/* Cost */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xxs}>
                <FormattedMessage
                  defaultMessage="Cost: {eligibleAmount}"
                  values={{
                    eligibleAmount: intl.formatNumber(transaction.eligibleAmount / 100, {
                      style: "currency",
                      currency: transaction.currencyCode,
                      currencyDisplay: "narrowSymbol",
                      maximumFractionDigits: 0
                    })
                  }}
                />
              </Typography>
              <Typography variant="body1" marginBottom={theme.tokens.spacing.xxs}>
                <FormattedMessage
                  defaultMessage="Carrot pays: {amount}"
                  values={{
                    amount: intl.formatNumber(transaction.amount / 100, {
                      style: "currency",
                      currency: transaction.currencyCode,
                      currencyDisplay: "narrowSymbol",
                      maximumFractionDigits: 0
                    })
                  }}
                />
              </Typography>
              <Typography variant="body1" marginBottom={theme.tokens.spacing.xxs}>
                <FormattedMessage
                  defaultMessage="You pay: {memberAmount}"
                  values={{
                    memberAmount: intl.formatNumber((transaction.eligibleAmount - transaction.amount) / 100, {
                      style: "currency",
                      currency: transaction.currencyCode,
                      currencyDisplay: "narrowSymbol",
                      maximumFractionDigits: 0
                    })
                  }}
                />
              </Typography>
              <Box paddingLeft={theme.tokens.spacing.sm}>
                <UnorderedList disablePadding={true}>
                  {amountAppliedToDeductibleString && (
                    <UnorderedListItem disablePadding={true}>
                      <ListItemText
                        primary={intl.formatMessage(
                          {
                            defaultMessage: "Applied to deductible: {amountAppliedToDeductibleString}"
                          },
                          {
                            amountAppliedToDeductibleString
                          }
                        )}
                      />
                    </UnorderedListItem>
                  )}
                  {amountAppliedToCostShareString && (
                    <UnorderedListItem disablePadding={true}>
                      <ListItemText
                        primary={intl.formatMessage(
                          {
                            defaultMessage: "Applied to cost share: {amountAppliedToCostShareString}"
                          },
                          {
                            amountAppliedToCostShareString
                          }
                        )}
                      />
                    </UnorderedListItem>
                  )}
                  {amountOverAvailableFundsString && (
                    <UnorderedListItem disablePadding={true}>
                      <ListItemText
                        primary={intl.formatMessage(
                          {
                            defaultMessage: "Amount over available funds: {amountOverAvailableFundsString}"
                          },
                          {
                            amountOverAvailableFundsString
                          }
                        )}
                      />
                    </UnorderedListItem>
                  )}
                </UnorderedList>
              </Box>
            </Box>

            {/* Service Start Date */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Service start date" />
              </Typography>
              <Typography variant="body1">
                <FormattedDate value={serviceDateStringToDate} year="numeric" month="long" day="2-digit" />
              </Typography>
            </Box>

            {/* Who Received Care */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Care recipient" />
              </Typography>
              <Typography variant="body1">{getWhoReceivedCare()}</Typography>
            </Box>

            {/* Service */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Service" />
              </Typography>
              <Typography variant="body1">{transaction.directPayServiceName}</Typography>
            </Box>

            {/* Provider */}
            {transaction.providerName && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Provider" />
                </Typography>
                <Typography variant="body1">{transaction.providerName}</Typography>
              </Box>
            )}

            {/* Infertility diagnosis */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Infertility diagnosis" />
              </Typography>
              <Typography variant="body1">
                {transaction.isInfertilityFlag ? (
                  <FormattedMessage defaultMessage="Yes" />
                ) : (
                  <FormattedMessage defaultMessage="No" />
                )}
              </Typography>
            </Box>

            {/* Medical necessity */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Medical necessity" />
              </Typography>
              <Typography variant="body1">
                {transaction.isMedicalNecessityFlag ? (
                  <FormattedMessage defaultMessage="Yes" />
                ) : (
                  <FormattedMessage defaultMessage="No" />
                )}
              </Typography>
            </Box>

            {/* Attachments */}
            {!transaction.isBenefitTransfer && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Documents" />
                </Typography>
                <FileLinks />
              </Box>
            )}
          </Stack>
        ) : null}

        {/* Merchant - Card expenses only */}
        {isCardExpense && transaction.merchantName && (
          <Box>
            <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
              <FormattedMessage defaultMessage="Merchant" />
            </Typography>
            <Typography variant="body1">{transaction.merchantName}</Typography>
          </Box>
        )}
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ marginBlockStart: theme.tokens.spacing.lg }}>
        <Divider sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.lg })} />
        <Box display="flex" justifyContent="flex-end">
          <Button
            color="secondary"
            variant="outlined"
            onClick={onClose}
            sx={() => ({
              minInlineSize: "120px"
            })}
          >
            <FormattedMessage defaultMessage="Close" />
          </Button>
        </Box>
      </Box>
    </Drawer>
  )
}
